import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import {
  FaExclamationTriangle,
  FaClipboardList,
  FaPlus,
  FaCalendarAlt,
  FaBoxOpen,
  FaKey,
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaIndustry,
} from 'react-icons/fa';
import { hesaplaLupamatKodu } from '../utils/lupamatHelper';

const Dashboard = () => {
  const navigate = useNavigate();

  const [yakindaBakimListesi, setYakindaBakimListesi] = useState([]);
  const [stokHataListesi, setStokHataListesi] = useState([]);
  const [lupamatEkranKodu, setLupamatEkranKodu] = useState('');
  const [lupamatGeriKod, setLupamatGeriKod] = useState('');
  const [kopyalandi, setKopyalandi] = useState(false);
  const [toplamKompresor, setToplamKompresor] = useState(0);
  const [aktifBakimSayisi, setAktifBakimSayisi] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bakimRes, stokRes, kompresorRes] = await Promise.all([
          fetch('/api/bakim'),
          fetch('/api/stok'),
          fetch('/api/kayitlar?page=1&limit=1'),
        ]);

        if (!bakimRes.ok || !stokRes.ok || !kompresorRes.ok) {
          throw new Error('Dashboard verileri alınamadı.');
        }

        const [bakimlar, stoklar, kompresorOzet] = await Promise.all([
          bakimRes.json(),
          stokRes.json(),
          kompresorRes.json(),
        ]);

        if (!Array.isArray(bakimlar) || !Array.isArray(stoklar)) {
          throw new Error('Bakım veya stok verisi beklenen formatta değil.');
        }

        if (
          !Number.isInteger(kompresorOzet?.total) ||
          kompresorOzet.total < 0
        ) {
          throw new Error('Kompresör toplamı beklenen formatta değil.');
        }

        const bugun = new Date();
        bugun.setHours(0, 0, 0, 0);

        const bekleyenBakimlar = bakimlar.filter(
          b => b.aktif !== false && b.durum === 'Planlı'
        );

        const yakinBakimlar = bekleyenBakimlar
          .map(b => {
            const bakimTarihi = new Date(b.tarih);
            bakimTarihi.setHours(0, 0, 0, 0);

            const gunFarki = Math.floor(
              (bakimTarihi - bugun) / (1000 * 60 * 60 * 24)
            );

            return { ...b, gunFarki };
          })
          .filter(b => b.gunFarki <= 15 && b.gunFarki >= -3)
          .sort((a, b) => a.gunFarki - b.gunFarki);

        const stoktaAzalanlar = stoklar
          .map(p => {
            const stok = parseInt(p.stokAdet || p.stok || 0);
            const min = parseInt(p.minStok || 0);

            return { ...p, stok, min };
          })
          .filter(p => !isNaN(p.min) && p.stok <= p.min)
          .sort((a, b) => a.min - a.stok);

        setYakindaBakimListesi(yakinBakimlar);
        setStokHataListesi(stoktaAzalanlar);
        setAktifBakimSayisi(bekleyenBakimlar.length);

        // Bakım sayısı yerine kompresör kayıtlarının toplamını kullan.
        setToplamKompresor(kompresorOzet.total);
      } catch (err) {
        console.error('Veri çekilirken hata oluştu:', err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (lupamatEkranKodu) {
      const sonuc = hesaplaLupamatKodu(lupamatEkranKodu, 'lcs500');
      setLupamatGeriKod(sonuc);
    } else {
      setLupamatGeriKod('');
    }

    setKopyalandi(false);
  }, [lupamatEkranKodu]);

  const handleKopyala = () => {
    if (lupamatGeriKod) {
      navigator.clipboard.writeText(lupamatGeriKod);
      setKopyalandi(true);
      setTimeout(() => setKopyalandi(false), 2000);
    }
  };

  const getGunFarkiColor = (gunFarki) => {
    if (gunFarki < 0) return '#ef4444';
    if (gunFarki <= 3) return '#f59e0b';
    if (gunFarki <= 7) return '#f97316';
    return '#10b981';
  };

  return (
    <div className="dashboard-modern">
      {/* Hero Section */}
      <div className="dashboard-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="gradient-text">MOON X24</span> Kontrol Paneli
          </h1>
          <p className="hero-subtitle">
            Kompresör bakım, stok ve kod üretim sistemine hoş geldiniz
          </p>
        </div>
        <div className="hero-decoration">
          <FaIndustry className="hero-icon" />
        </div>
      </div>

      {/* İstatistik Kartları */}
      <div className="stats-grid">
        <div className="stat-card stat-blue">
          <div className="stat-icon">
            <FaIndustry />
          </div>
          <div className="stat-content">
            <span className="stat-value">{toplamKompresor}</span>
            <span className="stat-label">Toplam Kompresör</span>
          </div>
        </div>

        <div className="stat-card stat-orange">
          <div className="stat-icon">
            <FaCalendarAlt />
          </div>
          <div className="stat-content">
            <span className="stat-value">{aktifBakimSayisi}</span>
            <span className="stat-label">Planlı Bakım</span>
          </div>
        </div>

        <div className="stat-card stat-red">
          <div className="stat-icon">
            <FaExclamationTriangle />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stokHataListesi.length}</span>
            <span className="stat-label">Kritik Stok</span>
          </div>
        </div>

        <div className="stat-card stat-green">
          <div className="stat-icon">
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <span className="stat-value">{yakindaBakimListesi.length}</span>
            <span className="stat-label">Yaklaşan Bakım</span>
          </div>
        </div>
      </div>

      {/* Ana İçerik Grid */}
      <div className="main-content-grid">
        {/* Lupamat Kod Üretici - Sol Taraf */}
        <div className="content-section lupamat-section">
          <div className="section-header">
            <div className="section-icon-wrapper">
              <FaKey className="section-icon" />
            </div>
            <h2>Lupamat Kod Üretici</h2>
            <span className="section-badge">LCS500 Parametre</span>
          </div>

          <div className="lupamat-container">
            <div className="input-wrapper">
              <label className="input-label">Ekran Kodu</label>
              <input
                type="number"
                placeholder="7020 gibi bir değer girin"
                value={lupamatEkranKodu}
                onChange={(e) => setLupamatEkranKodu(e.target.value)}
                className="modern-input"
                min="0"
                max="9999"
              />
            </div>

            <div className="result-wrapper">
              <label className="input-label">Geri Kod</label>
              <div
                className={`result-display ${
                  lupamatGeriKod ? 'active' : ''
                }`}
              >
                <span className="result-code">
                  {lupamatGeriKod || '----'}
                </span>

                {lupamatGeriKod && (
                  <button
                    className={`copy-btn ${kopyalandi ? 'copied' : ''}`}
                    onClick={handleKopyala}
                  >
                    {kopyalandi ? (
                      <FaCheckCircle />
                    ) : (
                      <FaClipboardList />
                    )}
                    {kopyalandi ? 'Kopyalandı' : 'Kopyala'}
                  </button>
                )}
              </div>
            </div>

            <button
              className="full-screen-btn"
              onClick={() => navigate('/lupamat-kod-uretici')}
            >
              Tam Ekran Üretici <FaArrowRight />
            </button>
          </div>
        </div>

        {/* Yaklaşan Bakımlar - Sağ Üst */}
        <div className="content-section">
          <div className="section-header">
            <div className="section-icon-wrapper orange">
              <FaClock className="section-icon" />
            </div>
            <h2>Yaklaşan Bakımlar</h2>
          </div>

          <div className="bakim-list-container">
            {yakindaBakimListesi.length > 0 ? (
              <div className="scrollable-list">
                {yakindaBakimListesi.slice(0, 6).map((bakim, index) => (
                  <div key={index} className="bakim-item">
                    <div className="bakim-info">
                      <h4 className="bakim-firma">{bakim.firma}</h4>
                      <p className="bakim-model">
                        {bakim.kompresorModel || 'Belirtilmemiş'}
                      </p>
                    </div>

                    <div className="bakim-date">
                      <span className="date-text">
                        {new Date(bakim.tarih).toLocaleDateString('tr-TR')}
                      </span>
                      <span
                        className="days-badge"
                        style={{
                          backgroundColor: getGunFarkiColor(bakim.gunFarki),
                          color:
                            bakim.gunFarki < 0 || bakim.gunFarki <= 3
                              ? 'white'
                              : 'black',
                        }}
                      >
                        {bakim.gunFarki >= 0
                          ? `${bakim.gunFarki} gün`
                          : 'Geçmiş'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FaCheckCircle className="empty-icon" />
                <p>Yaklaşan bakım bulunmuyor</p>
              </div>
            )}
          </div>
        </div>

        {/* Kritik Stoklar - Sağ Alt */}
        <div className="content-section">
          <div className="section-header">
            <div className="section-icon-wrapper red">
              <FaBoxOpen className="section-icon" />
            </div>
            <h2>Kritik Stoklar</h2>
          </div>

          <div className="stok-list-container">
            {stokHataListesi.length > 0 ? (
              <div className="scrollable-list">
                {stokHataListesi.slice(0, 6).map((parca, index) => (
                  <div key={index} className="stok-item">
                    <div className="stok-info">
                      <h4 className="stok-ad">{parca.ad}</h4>
                      <p className="stok-kod">{parca.parcaKodu}</p>
                    </div>

                    <div className="stok-level">
                      <div className="stock-bar">
                        <div
                          className="stock-fill"
                          style={{
                            width: `${Math.min(
                              100,
                              (parca.stok / parca.min) * 100
                            )}%`,
                            backgroundColor:
                              parca.stok === 0 ? '#ef4444' : '#f59e0b',
                          }}
                        />
                      </div>
                      <span className="stock-text">
                        {parca.stok} / {parca.min}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FaCheckCircle className="empty-icon" />
                <p>Tüm stoklar yeterli</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hızlı Erişim Butonları */}
      <div className="quick-actions">
        <h3 className="actions-title">Hızlı Erişim</h3>
        <div className="actions-grid">
          <button
            className="action-btn action-primary"
            onClick={() => navigate('/teklif-gonder')}
          >
            <FaPlus className="action-icon" />
            <span>Yeni Teklif</span>
          </button>

          <button
            className="action-btn action-warning"
            onClick={() => navigate('/bakim-planlayici')}
          >
            <FaCalendarAlt className="action-icon" />
            <span>Bakım Planla</span>
          </button>

          <button
            className="action-btn action-success"
            onClick={() => navigate('/stok-takip')}
          >
            <FaBoxOpen className="action-icon" />
            <span>Stok Yönet</span>
          </button>

          <button
            className="action-btn action-info"
            onClick={() => navigate('/lupamat-kod-uretici')}
          >
            <FaKey className="action-icon" />
            <span>Kod Üret</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;